import React, { useRef, useState, useMemo, useCallback } from 'react';
import TmReactTable from '../../components/utilComponents/tmReactTable/tmReactTable';
import TmExportExcel from '../../components/utilComponents/tmExportExcel/tmExportExcel';
import moment from 'moment';
import { isManagement } from '../../util/customMethods';

/**
 * SearchInvoice (v2)
 *
 * Changes:
 *  - Functional component
 *  - Removed data mutation in render (was modifying props directly)
 *  - Removed href="javascript:void(0)" — plain <span> instead
 *  - Columns/export builders memoized
 *  - Single export builder handles both management and non-management
 *  - Removed unused Form wrapper from react-validation
 */

// ── Column definitions ──────────────────────────────────────────────────────

const CELL_STYLE = { textAlign: 'center', display: 'inline-grid' };

const EXPORT_FIELDS = [
    { key: 'displayInvoiceId', title: 'INVOICE #' },
    { key: 'companyName',      title: 'COMPANY',      mgmtOnly: true },
    { key: 'reason',           title: 'REASON' },
    { key: 'callCreationDate', title: 'TOW DATE' },
    { key: 'driver',           title: 'DRIVER #' },
    { key: 'truck',            title: 'TRUCK #' },
    { key: 'towType',          title: 'TOW TYPE' },
    { key: 'requestedBy',      title: 'REQUESTED BY' },
    { key: 'oR_DR',            title: 'OR/DR#' },
    { key: 'vehicleYear',      title: 'YEAR' },
    { key: 'make',             title: 'MAKE' },
    { key: 'model',            title: 'MODEL' },
    { key: 'color',            title: 'COLOR' },
    { key: 'vehicleVin',       title: 'VIN #' },
    { key: 'vehiclePlateNo',   title: 'PLATE #' },
    { key: 'email',            title: 'EMAIL' },
    { key: 'phone',            title: 'PHONE' },
];

const buildColumns = (isMgmt) => {
    const cols = [
        {
            Header: 'INVOICE #', accessor: 'displayInvoiceId', style: CELL_STYLE,
            Cell: (row) => (
                <a href={`serviceCallInfo/${row.original.invoiceId}`} target="_blank" rel="noopener noreferrer">
                    {row.original.displayInvoiceId}
                </a>
            ),
        },
        { Header: 'COMPANY', accessor: 'companyName', style: CELL_STYLE, show: isMgmt },
        { Header: 'REASON', accessor: 'reason', style: CELL_STYLE },
        { Header: 'TOW DATE', accessor: 'callCreationDate', style: CELL_STYLE },
        { Header: 'DRIVER #', accessor: 'driver', style: CELL_STYLE },
        { Header: 'TRUCK #', accessor: 'truck', style: CELL_STYLE },
        { Header: 'TOW TYPE', accessor: 'towType', style: CELL_STYLE },
        { Header: 'REQUESTED BY', accessor: 'requestedBy', style: CELL_STYLE },
        { Header: 'OR/DR#', accessor: 'oR_DR', style: CELL_STYLE },
        { Header: 'YEAR', accessor: 'vehicleYear', style: CELL_STYLE },
        { Header: 'MAKE', accessor: 'make', style: CELL_STYLE },
        { Header: 'MODEL', accessor: 'model', style: CELL_STYLE },
        { Header: 'COLOR', accessor: 'color', style: CELL_STYLE },
        { Header: 'VIN #', accessor: 'vehicleVin', style: CELL_STYLE },
        { Header: 'PLATE #', accessor: 'vehiclePlateNo', style: CELL_STYLE },
        { Header: 'EMAIL', accessor: 'email', style: CELL_STYLE },
        { Header: 'PHONE', accessor: 'phone', style: CELL_STYLE },
    ];
    return cols;
};

// ── Component ───────────────────────────────────────────────────────────────

const SearchInvoice = ({ data = [] }) => {
    const isMgmt = isManagement();
    const tableRef = useRef(null);
    const [sortedData, setSortedData] = useState([]);

    const columns = useMemo(() => buildColumns(isMgmt), [isMgmt]);

    const buildExportData = useCallback(() => {
        const fields = EXPORT_FIELDS.filter((f) => !f.mgmtOnly || isMgmt);
        // Use sorted/filtered data if available, else raw data
        const source = sortedData.length > 0 ? sortedData : data;
        return [{
            columns: fields.map((f) => ({ title: f.title })),
            data: source.map((item) =>
                fields.map((f) => ({ value: (item[f.key] != null ? item[f.key] : '') }))
            ),
        }];
    }, [data, sortedData, isMgmt]);

    const handleFilterChange = useCallback(() => {
        if (!tableRef.current || !tableRef.current.tmReactTable) return;
        const resolved = tableRef.current.tmReactTable.getResolvedState().sortedData;
        const mapped = resolved.map((item) => item._original);
        setSortedData(mapped);
    }, []);

    return (
        <div className="pd-10">
            <fieldset>
                <div className="row">
                    <div className="col-md-12 col-sm-12 mb-10">
                        <TmExportExcel
                            name="Invoice Search"
                            filename={`InvoiceSearch_${moment().format('MMDDYYhhmm')}`}
                            fileExtension="xlsx"
                            element={
                                <button className="btn btn-warning btn-sm pull-right">
                                    <i className="fa fa-file-excel-o" /> Export
                                </button>
                            }
                            dataSet={buildExportData()}
                        />
                    </div>
                    <div className="col-md-12 col-sm-12 mb-10">
                        <TmReactTable
                            filterable sortable resizable
                            defaultPageSize={25}
                            data={data}
                            onFilteredChange={handleFilterChange}
                            columns={columns}
                            ref={tableRef}
                        />
                    </div>
                </div>
            </fieldset>
        </div>
    );
};

export default React.memo(SearchInvoice);
